import { GLOSSARY } from "@/lib/glossary";

// Glossary groups shown on /glossary (first matching rule wins).
export const GROUPS = [
  ["Opportunities scanner", (k) => k.startsWith("opp_") || ["scanner", "regime", "market_structure", "adx", "var", "vwap", "decision_comparison", "entry"].includes(k)],
  ["Prediction ledger & transparency", (k) => /^(ledger_|recorder_|coverage_|weekly_)/.test(k) || ["pas", "predictions_evaluated", "directional_accuracy", "outcome", "status_open", "status_completed", "status_invalidated", "status_replaced", "hit_rate", "total_signals", "aged_signals", "avg_return", "by_strength", "by_coin", "win_rate", "max_drawdown", "delta_since", "timeline"].includes(k)],
  ["Research statistics", (k) => k.startsWith("stat_")],
  ["AI signals", (k) => ["outlook", "strength", "signal_strength", "risk", "timeframes", "evidence_for", "evidence_against", "invalidation", "key_levels", "similar", "waiting_for", "recheck", "arm_alert", "horizon", "analyze", "momentum_col", "strength_col"].includes(k)],
  ["Indicators & charts", (k) => /^(range_|toggle_|sma|ema|bb_|rsi|macd|bollinger|candles|live_chart|order_book|momentum$|volatility$|support|resistance|volume|sr_break)/.test(k) || ["support_resistance", "price_above", "price_below", "range_enter", "range_exit", "volume_spike", "volatility_alert"].includes(k)],
  ["Market data", (k) => ["market_cap", "volume_24h", "circulating", "ath", "total_market_cap", "global_volume", "btc_dominance", "active_coins", "fear_greed", "gainers", "losers", "trending", "price_change_24h", "price_live_snapshot", "spark_7d", "watchlist", "watchlist_card", "search"].includes(k)],
  ["Paper trading & portfolio", (k) => k.startsWith("pt_") || ["paper_trade", "paper_trading", "pnl", "reset", "total_value", "cash", "holdings_value", "realized_pnl", "buy_more", "sell_pos", "buy", "sell", "portfolio_value"].includes(k)],
  ["Alerts & notifications", (k) => /^(signal_change|signal_conditions|alert_expiry|push_notifications|create_alert|alerts_overview|bracket_alerts|notifications)$/.test(k)],
  ["Plans & navigation", () => true],
];
export const groupOf = (k) => GROUPS.find(([, f]) => f(k))[0];

// Glossary entry → the Learn Crypto lesson that teaches it. Slugs are the seeded lesson slugs (backend/learn.py seed; slugs are immutable).
export const LESSONS = {
  "why-prices-move": "Why crypto prices move",
  "reading-a-chart": "How to read a crypto chart",
  "buying-selling-basics": "Buying, selling and placing orders",
  "understanding-signals": "Understanding EV-IDEX signals: buy, sell, wait",
  "how-ai-predictions-work": "How EV-IDEX AI predictions work and are graded",
  "technical-indicators": "Technical indicators: RSI, MACD, moving averages, Bollinger Bands",
  "support-resistance-and-levels": "Support, resistance, entries, targets and stops",
  "risk-management": "Managing financial risk",
  "profits-losses-and-pnl": "Understanding profits, losses and P&L",
  "market-trends-and-cycles": "Market trends, cycles and timeframes",
  "trading-strategies-and-backtesting": "Trading strategies and backtesting",
  "evidex-features-glossary": "Every EV-IDEX feature and term, explained",
};

const KEY_RULES = [
  [/^(support|resistance|support_resistance|key_levels|entry|sr_break|price_above|price_below|range_enter|range_exit|opp_(entry|target|invalidation|rr))/, "support-resistance-and-levels"],
  [/^(risk|var|max_drawdown|stat_maxdd|opp_risk|volatility|volatility_alert|pt_(stop_loss|take_profit|trail_pct|oco))/, "risk-management"],
  [/^(pnl|realized_pnl|total_value|holdings_value|cash|portfolio_value|pt_(position_pnl|unrealised_pnl|win_rate|avg_cost|avg_entry|position_value|position_value_sell))$/, "profits-losses-and-pnl"],
  [/^(candles|live_chart|order_book|range_|spark_7d|price_live_snapshot|toggle_)/, "reading-a-chart"],
  [/^(regime|market_structure|timeframes|horizon|opp_(regime|horizon|timeframe|freshness)|timeline)/, "market-trends-and-cycles"],
  [/^(stat_|by_strength|by_coin|avg_return|hit_rate|win_rate|total_signals|aged_signals)/, "trading-strategies-and-backtesting"],
];
const GROUP_RULES = {
  "Opportunities scanner": "understanding-signals",
  "Prediction ledger & transparency": "how-ai-predictions-work",
  "Research statistics": "trading-strategies-and-backtesting",
  "AI signals": "understanding-signals",
  "Indicators & charts": "technical-indicators",
  "Market data": "why-prices-move",
  "Paper trading & portfolio": "buying-selling-basics",
  "Alerts & notifications": "evidex-features-glossary",
  "Plans & navigation": "evidex-features-glossary",
};

export const lessonFor = (k, group) => {
  const slug = (KEY_RULES.find(([re]) => re.test(k)) || [])[1] || GROUP_RULES[group];
  return slug ? { slug, title: LESSONS[slug] } : null;
};

// Reverse mapping for a lesson page: every glossary term whose "Learn more" points at this lesson.
export const termsForLesson = (slug) => Object.entries(GLOSSARY)
  .filter(([k]) => lessonFor(k, groupOf(k))?.slug === slug)
  .map(([k, [title]]) => ({ k, title }))
  .sort((a, b) => a.title.localeCompare(b.title));
